import type { Device } from "@models";
import {
  AddRounded,
  CheckCircle,
  Delete,
  DevicesOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Skeleton, Typography } from "@mui/material";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { DemoChip } from "src/components/DemoChip/DemoChip";

type DeviceManagementSectionProps = {
  devices: Device[];
  isLoading: boolean;
  activeDeviceId: string | null;
  onAddDevice: () => void;
  onDeleteDevice: (deviceId: string) => void;
  onSelectDevice: (deviceId: string | null) => void;
};

const skeletonRowIds = [
  "device-management-skeleton-1",
  "device-management-skeleton-2",
  "device-management-skeleton-3",
];

const getDeviceRowClassName = (isSelected: boolean) =>
  [
    "flex w-full cursor-pointer items-center justify-between rounded border border-solid p-3 text-left transition-colors",
    isSelected
      ? "border-color-primary-darkest bg-color-primary-lightest"
      : "border-gray-200 bg-white hover:bg-zinc-50",
  ].join(" ");

export const DeviceManagementSection = ({
  devices,
  isLoading,
  activeDeviceId,
  onAddDevice,
  onDeleteDevice,
  onSelectDevice,
}: DeviceManagementSectionProps) => {
  const handleSelectDevice = (deviceId: string) => {
    onSelectDevice(activeDeviceId === deviceId ? null : deviceId);
  };

  const handleDeviceKeyDown = (
    event: ReactKeyboardEvent<HTMLDivElement>,
    deviceId: string
  ) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleSelectDevice(deviceId);
  };

  return (
    <section className="flex flex-col gap-3 py-2 px-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <DevicesOutlined />
          <Typography variant="body1" data-testid="device-management-subheader">
            Device management
          </Typography>
        </div>
        {devices.length > 0 && (
          <Button
            color="inherit"
            onClick={onAddDevice}
            size="small"
            startIcon={<AddRounded />}
            variant="text"
          >
            Add Device
          </Button>
        )}
      </div>

      {isLoading && (
        <div
          className="flex flex-col gap-2"
          data-testid="device-management-loading"
        >
          {skeletonRowIds.map((rowId) => (
            <Skeleton key={rowId} variant="rectangular" height={52} />
          ))}
        </div>
      )}

      {!isLoading && devices.length === 0 && (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 rounded border border-solid border-gray-200 bg-white p-4 text-center">
          <Typography variant="body2" color="text.secondary">
            No devices yet.
          </Typography>
          <Button
            color="primary"
            onClick={onAddDevice}
            startIcon={<AddRounded />}
            variant="contained"
          >
            Add Device
          </Button>
        </div>
      )}

      {!isLoading && devices.length > 0 && (
        <div className="max-h-[300px] min-h-0 overflow-y-auto pr-1">
          <div className="flex flex-col gap-2" data-testid="device-list">
            {devices.map((device) => {
              const isSelected = activeDeviceId === device.id;

              return (
                <div
                  key={device.id}
                  aria-label={`${isSelected ? "Unselect" : "Select"} ${
                    device.name
                  }`}
                  aria-pressed={isSelected}
                  className={getDeviceRowClassName(isSelected)}
                  onClick={() => handleSelectDevice(device.id)}
                  onKeyDown={(event) => handleDeviceKeyDown(event, device.id)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {device.isDemoDevice && <DemoChip size="tiny" />}
                    <Typography variant="body1" className="min-w-0 truncate">
                      {device.name}
                    </Typography>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {isSelected && (
                      <CheckCircle
                        aria-hidden
                        color="primary"
                        fontSize="small"
                      />
                    )}
                    <IconButton
                      aria-label={`Delete ${device.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteDevice(device.id);
                      }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};
